// src/frontend_react/src/pages/AdminDashboard/MockDataGeneratorPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';

// Base UI Components
import Button from '../../components/ui/Button/Button';
import Input from '../../components/ui/Input/Input';
import Select from '../../components/ui/Select/Select';
import Checkbox from '../../components/ui/Checkbox/Checkbox';
import Alert from '../../components/ui/Alert/Alert';
import Spinner from '../../components/ui/Spinner/Spinner';
import Card from '../../components/ui/Card/Card';
import Icon from '../../components/ui/Icon/Icon';
import Typography from '../../components/ui/Typography/Typography';
import Stack, { HStack } from '../../components/ui/Stack/Stack';
import Box from '../../components/ui/Box/Box';

// API service
import {
  getAdminProjects,
  generateMockData,
  downloadRawMockFile,
  downloadAllRawMockFilesAsZip,
} from '../../services/api';

// Utilities
import { notify } from '../../services/notificationService';
import { triggerBrowserDownload } from '../../utils/fileDownloadHelper';

// Import shared admin styles
import adminPageStyles from './styles.module.css';

// Icons
import { FiSettings, FiFileText, FiDownload, FiDownloadCloud } from 'react-icons/fi';

function MockDataGeneratorPage() {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [projectError, setProjectError] = useState(null);

  const [numRowsInsurer, setNumRowsInsurer] = useState(70);
  const [numCoreMatchingIndividuals, setNumCoreMatchingIndividuals] =
    useState(45);
  const [numUniquePerAdditionalDataset, setNumUniquePerAdditionalDataset] =
    useState(10);
  const [numFilesPerSource, setNumFilesPerSource] = useState(1);

  const [generateInsurerData, setGenerateInsurerData] = useState(true);
  const [generateNaluriData, setGenerateNaluriData] = useState(true);
  const [generateCorporateData, setGenerateCorporateData] = useState(true);

  const [isGenerating, setIsGenerating] = useState(false);
  const [generationResults, setGenerationResults] = useState(null);
  const [error, setError] = useState(null);

  const clearMessagesAndResults = useCallback(() => {
    setError(null);
    setGenerationResults(null);
  }, []);

  const fetchProjects = useCallback(async () => {
    setLoadingProjects(true);
    setProjectError(null);
    clearMessagesAndResults();
    try {
      const projectsData = await getAdminProjects();
      const sortedProjects = Array.isArray(projectsData)
        ? projectsData
            .filter((p) => p.is_active)
            .sort((a, b) => a.name.localeCompare(b.name))
        : [];
      setProjects(sortedProjects);
      if (sortedProjects.length > 0) {
        setSelectedProjectId(sortedProjects[0].id);
      } else {
        setProjectError(
          'No active projects available. Please create or activate a project first.',
        );
        setSelectedProjectId('');
      }
    } catch (err) {
      setProjectError(
        err.message || 'An error occurred while loading projects.',
      );
      setProjects([]);
      setSelectedProjectId('');
    } finally {
      setLoadingProjects(false);
    }
  }, [clearMessagesAndResults]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    clearMessagesAndResults();
  }, [selectedProjectId, clearMessagesAndResults]);

  const handleGenerateClick = async () => {
    if (!selectedProjectId) {
      setError('Please select a project first.');
      return;
    }
    clearMessagesAndResults();
    setIsGenerating(true);
    const options = {
      project_id: selectedProjectId,
      num_rows_insurer: parseInt(numRowsInsurer, 10) || 0,
      num_core_matching_individuals:
        parseInt(numCoreMatchingIndividuals, 10) || 0,
      num_unique_per_additional_dataset:
        parseInt(numUniquePerAdditionalDataset, 10) || 0,
      num_files_per_source: parseInt(numFilesPerSource, 10) || 1,
      generate_insurer_data: generateInsurerData,
      generate_n_data: generateNaluriData,
      generate_corporate_data: generateCorporateData,
    };
    try {
      const result = await generateMockData(options);
      setGenerationResults(result);
      if (result && result.success) {
        notify.success(
          result.message || 'Sample datasets generated successfully!',
        );
      } else {
        setError(
          result?.message || 'Sample dataset generation completed with issues.',
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Failed to generate sample datasets due to an API error.',
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadMutation = useMutation({
    mutationFn: ({ projectId, fileId, filename }) =>
      downloadRawMockFile(projectId, fileId, filename),
    onSuccess: (data) => {
      if (data.download_url) {
        // S3 case
        triggerBrowserDownload(data.download_url, data.suggested_filename);
      } else if (data.fileBlob) {
        // Local dev case
        triggerBrowserDownload(data.fileBlob, data.filename);
      } else {
        notify.error('Could not download file: Invalid response from server.');
        return;
      }
      notify.success('Download initiated!');
    },
    onError: (error) => {
      notify.error(error.message || 'Failed to start download.');
    },
  });

  const downloadAllMutation = useMutation({
    mutationFn: ({ projectId, fileIds }) => downloadAllRawMockFilesAsZip(projectId, fileIds),
    onSuccess: (data) => {
      // The API service now *always* returns a blob and filename for this endpoint
      triggerBrowserDownload(data.fileBlob, data.filename);
      notify.success('ZIP file download initiated!');
    },
    onError: (error) => {
      notify.error(error.message || 'Failed to create ZIP file for download.');
    }
  });

  const handleDownloadAll = () => {
    if (!generationResults?.generated_files_details || generationResults.generated_files_details.length === 0) {
      notify.warn("No files available to download.");
      return;
    }
    const fileIdsToDownload = generationResults.generated_files_details.map(f => f.file_id);
    downloadAllMutation.mutate({ projectId: selectedProjectId, fileIds: fileIdsToDownload });
  };

  const projectOptions = projects.map((proj) => ({
    value: proj.id,
    label: `${proj.name} (ID: ${proj.id})`,
  }));
  const currentProjectName =
    projects.find((p) => p.id === selectedProjectId)?.name || selectedProjectId;

  if (loadingProjects && projects.length === 0) {
    return (
      <Card
        title="Generate Mock Data (Admin)"
        titleIcon={<Icon as={FiSettings} />}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py="xl"
        >
          <Spinner size="lg" />
          <Typography mt="md" color="secondary">
            Loading projects...
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      title="Generate Mock Data (Admin)"
      titleIcon={<Icon as={FiSettings} />}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleGenerateClick();
        }}
      >
        <Stack spacing="xl">
          <Typography variant="body1" color="secondary">
            This tool creates sample CSV files for testing platform workflows.
            Files are placed in the selected project's 'raw_data' directory on
            the server.
          </Typography>

          {error && (
            <Alert
              variant="error"
              title="Generation Error"
              onDismiss={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          <Select
            label="Target Project"
            id="projectSelectAdminMockData"
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            options={projectOptions}
            placeholder={
              projects.length > 0
                ? '-- Select a Project --'
                : 'No active projects available'
            }
            disabled={loadingProjects || projects.length === 0 || isGenerating}
            error={projectError ? String(projectError) : undefined}
            required
          />

          <Input
            label="Total Rows for 'Insurer' Dataset:"
            id="numRowsInsurer"
            type="number"
            value={numRowsInsurer}
            onChange={(e) => setNumRowsInsurer(e.target.value)}
            disabled={isGenerating}
            min="0"
          />
          <Input
            label="Core Matching Individuals (shared across datasets):"
            id="numCoreMatching"
            type="number"
            value={numCoreMatchingIndividuals}
            onChange={(e) => setNumCoreMatchingIndividuals(e.target.value)}
            disabled={isGenerating}
            min="0"
            hintText="Basis for Hashed_ID linkage."
          />
          <Input
            label="Unique Individuals per Additional Dataset (Naluri, Corporate):"
            id="numUniqueAdditional"
            type="number"
            value={numUniquePerAdditionalDataset}
            onChange={(e) => setNumUniquePerAdditionalDataset(e.target.value)}
            disabled={isGenerating}
            min="0"
            hintText="Added on top of the core set for these datasets."
          />
          <Input
            label="Number of Files per Data Source"
            id="numFilesPerSource"
            type="number"
            value={numFilesPerSource}
            onChange={(e) => setNumFilesPerSource(e.target.value)}
            disabled={isGenerating}
            min="1"
            hintText="Split the total rows for each source into this many files."
          />
          <Box>
            <Typography variant="body1" fontWeight="medium" gutterBottom>
              Datasets to Generate:
            </Typography>
            <HStack spacing="lg" wrap="wrap">
              <Checkbox
                label="'Insurer' Sample"
                id="genInsurer"
                checked={generateInsurerData}
                onChange={(e) => setGenerateInsurerData(e.target.checked)}
                disabled={isGenerating}
              />
              <Checkbox
                label="'Naluri' Sample"
                id="genNaluri"
                checked={generateNaluriData}
                onChange={(e) => setGenerateNaluriData(e.target.checked)}
                disabled={isGenerating}
              />
              <Checkbox
                label="'Corporate' Sample"
                id="genCorporate"
                checked={generateCorporateData}
                onChange={(e) => setGenerateCorporateData(e.target.checked)}
                disabled={isGenerating}
              />
            </HStack>
          </Box>

          <Stack
            direction="row"
            pt="md"
            className={adminPageStyles.formActionRow}
          >
            <Button
              type="submit"
              disabled={isGenerating || !selectedProjectId || loadingProjects}
              variant="primary"
              leftIcon={<Icon as={FiFileText} />}
              isLoading={isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Generate Sample Datasets'}
            </Button>
          </Stack>
        </Stack>
      </form>

      {generationResults && (
        <Card
          title="Generation Task Output"
          mt="xl"
          elevation="sm"
          className={
            generationResults.success
              ? adminPageStyles.adminResultsFeedbackCardSuccess
              : adminPageStyles.adminResultsFeedbackCardError
          }
          headerActions={
            generationResults.success && generationResults.generated_files_details?.length > 0 && (
              <Button
                variant="primary"
                onClick={handleDownloadAll}
                leftIcon={<Icon as={FiDownloadCloud} />}
                disabled={downloadAllMutation.isPending}
                isLoading={downloadAllMutation.isPending}
              >
                Download All as .ZIP
              </Button>
            )
          }
        >
          <Stack spacing="md" p="md">
            <Typography>
              <strong>Status:</strong> {generationResults.message}
            </Typography>
            {generationResults.success && (
              <>
                <Typography>
                  <strong>Project:</strong>{' '}
                  {generationResults.project_name || currentProjectName}
                </Typography>
                <Typography>
                  <strong>Output Directory:</strong>
                  <Typography
                    component="code"
                    variant="caption"
                    color="secondary"
                    ml="xs"
                    style={{ wordBreak: 'break-all' }}
                  >
                    {generationResults.output_directory_relative}
                  </Typography>
                </Typography>
                {generationResults.generated_files_details &&
                  generationResults.generated_files_details.length > 0 && (
                    <Box>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        gutterBottom
                      >
                        Generated Files:
                      </Typography>
                      <Stack
                        spacing="sm"
                        as="ul"
                        style={{ listStyle: 'none', paddingLeft: '0' }}
                      >
                        {generationResults.generated_files_details.map(
                          (file, index) => (
                            <HStack
                              as="li"
                              key={index}
                              justifyContent="space-between"
                              alignItems="center"
                              p="xs"
                              style={{
                                borderBottom:
                                  '1px solid var(--cipher-color-border-subtle)',
                              }}
                            >
                              <Typography variant="body2">
                                <strong>'{file.file_type}' Dataset:</strong>{' '}
                                {file.filename} ({file.rows} rows)
                              </Typography>
                              <Button
                                variant="secondary"
                                size="sm"
                                leftIcon={<Icon as={FiDownload} />}
                                onClick={() =>
                                  downloadMutation.mutate({
                                    projectId: selectedProjectId,
                                    fileId: file.file_id,
                                    filename: file.filename, 
                                  })
                                }
                                isLoading={
                                  downloadMutation.isPending &&
                                  downloadMutation.variables?.fileId ===
                                    file.file_id
                                }
                                disabled={downloadMutation.isPending}
                              >
                                Download
                              </Button>
                            </HStack>
                          ),
                        )}
                      </Stack>
                    </Box>
                  )}
              </>
            )}
            {!generationResults.success && generationResults.error && (
              <Typography color="error">
                <strong>Error:</strong> {generationResults.error}
              </Typography>
            )}
          </Stack>
        </Card>
      )}
    </Card>
  );
}

export default MockDataGeneratorPage;
